import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import React from 'react'
import { PhotovoltaicFormData } from 'src/Libs/installation-statement/configurations/photovoltaic'
import LabeledFieldDisplay from 'src/Libs/installation-statement/components/Overview/LabeledFieldDisplay'
import { calculateKwp } from 'src/Libs/installation-statement/utils/photovoltaic'
import useLabeledPhotovoltaicOptions from '../../hooks/use-labled-photovoltaic-options'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'

interface Props {
  data: PhotovoltaicFormData
}

/**
 * Photovoltaic data to be displayed on overview step of the installation statement
 */
const Photovoltaic: React.FC<Props> = ({ data }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { directionOptions } = useLabeledPhotovoltaicOptions()

  return (
    <Box>
      <Typography variant="h4" sx={{ pb: 2 }}>
        {t('installation-statement.photovoltaic.title')}
      </Typography>
      {data.pvTrackers.map((item, index) => {
        const trackerNumeration = index + 1
        const availablePhotovoltaicStrings = item.pvStrings.slice(0, 3)
        const { appliedToAllString } = item
        const hasMultiplePhotovoltaicStrings = availablePhotovoltaicStrings.length > 1
        const desktopDirection = hasMultiplePhotovoltaicStrings ? 'row' : 'column'
        const direction = isMobile ? 'column' : desktopDirection

        return (
          <Box key={index} sx={{ pb: 2 }}>
            <Typography variant="h5" sx={{ pb: 2 }}>{`${t('installation-statement.photovoltaic.tab.title')} ${trackerNumeration}`}</Typography>
            <Box sx={{ pb: 2 }}>
              <LabeledFieldDisplay label={t('installation-statement.photovoltaic.form.tracker.name')} dataField={item.name} />
              <LabeledFieldDisplay label={t('installation-statement.photovoltaic.form.tracker.string-count')} dataField={item.pvStrings.length} />
            </Box>
            <Stack direction={direction} spacing={isMobile ? 2 : 4}>
              {availablePhotovoltaicStrings.map((s, index) => {
                const stringNumeration = index + 1
                const photovoltaicString = appliedToAllString ? availablePhotovoltaicStrings[0] : s
                const directionLabel =
                  directionOptions.find(option => option.value === photovoltaicString.direction)?.label ?? photovoltaicString.direction

                const kwp =
                  photovoltaicString.modulePeakPower && photovoltaicString.moduleCount
                    ? calculateKwp(photovoltaicString.moduleCount, Number(photovoltaicString.modulePeakPower))
                    : null

                return (
                  <Box key={`${item}-${index}`} flex={1}>
                    <Box key={index}>
                      <Box sx={{ pb: 2 }}>
                        <Typography variant="caption">
                          {t('installation-statement.photovoltaic.form.string.title')} {stringNumeration}
                        </Typography>
                      </Box>
                      <LabeledFieldDisplay
                        label={t('installation-statement.photovoltaic.form.string.module-type')}
                        dataField={photovoltaicString.pvModuleType}
                      />
                      <LabeledFieldDisplay
                        label={t('installation-statement.photovoltaic.form.string.module-count')}
                        dataField={photovoltaicString.moduleCount ? getLocalizedNumber(Number(photovoltaicString.moduleCount)) : null}
                      />
                      <LabeledFieldDisplay
                        label={t('installation-statement.photovoltaic.form.string.power-optimizer')}
                        dataField={photovoltaicString.powerOptimizer}
                      />
                      <LabeledFieldDisplay label={t('installation-statement.photovoltaic.form.string.direction')} dataField={directionLabel} />
                      <LabeledFieldDisplay
                        label={t('installation-statement.photovoltaic.form.string.orientation')}
                        dataField={photovoltaicString.orientation}
                      />
                      <LabeledFieldDisplay
                        label={t('installation-statement.photovoltaic.form.string.test-voltage')}
                        dataField={photovoltaicString.testVoltage ? getLocalizedNumber(Number(photovoltaicString.testVoltage)) : null}
                      />
                      <LabeledFieldDisplay
                        label={t('installation-statement.photovoltaic.form.string.module-peak-power')}
                        dataField={photovoltaicString.modulePeakPower ? getLocalizedNumber(Number(photovoltaicString.modulePeakPower)) : null}
                      />
                      <LabeledFieldDisplay
                        label={t('installation-statement.photovoltaic.form.string.open-circuit-voltage')}
                        dataField={photovoltaicString.openCircuitVoltage ? getLocalizedNumber(Number(photovoltaicString.openCircuitVoltage)) : null}
                      />
                      <LabeledFieldDisplay
                        label={t('installation-statement.photovoltaic.form.string.positive-electrode-ground')}
                        dataField={
                          photovoltaicString.positiveElectrodeGround ? getLocalizedNumber(Number(photovoltaicString.positiveElectrodeGround)) : null
                        }
                      />
                      <LabeledFieldDisplay
                        label={t('installation-statement.photovoltaic.form.string.negative-electrode-ground')}
                        dataField={
                          photovoltaicString.negativeElectrodeGround ? getLocalizedNumber(Number(photovoltaicString.negativeElectrodeGround)) : null
                        }
                      />
                      <LabeledFieldDisplay
                        label={t('installation-statement.photovoltaic.form.string.riso')}
                        dataField={photovoltaicString.riso ? getLocalizedNumber(Number(photovoltaicString.riso)) : null}
                      />
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="subtitle2">{t('installation-statement.photovoltaic.form.string.kwp')}</Typography>
                        {kwp && <Typography variant="subtitle2">{getLocalizedNumber(Number(kwp))}</Typography>}
                      </Box>
                    </Box>
                    {index < availablePhotovoltaicStrings.length - 1 && (
                      <Divider orientation={isMobile ? 'horizontal' : 'vertical'} flexItem sx={{ borderColor: theme.palette.shades.shade03 }} />
                    )}
                  </Box>
                )
              })}
            </Stack>
          </Box>
        )
      })}
      {data.pvTrackers.length === 0 && <Typography variant="subtitle2">{t('installation-statement.photovoltaic.no-tracker-provided')}</Typography>}
    </Box>
  )
}

export default Photovoltaic
